import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import Button from '../atoms/Button';
import Input from '../atoms/Input';
import Text from '../atoms/Text';
import IconClose from '../icons/IconClose';

export interface Props {
  currentPage: number;
  pageCount: number;
  onPageChanged: (page: number) => void;
}

const PaginationContainer = styled.div`
  color: ${({ theme }) => theme.colors.primaryPurple};
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  margin-top: 3rem;
`;

const PaginationButton = styled(Button)`
  background-color: transparent;
  border: none;
  min-width: 130px; /* magic number to ensure PaginationPageList remains centered */

  &:disabled {
    visibility: hidden;

    &:hover {
      background-color: transparent;
    }
  }
`;

const PaginationPageList = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
`;

const PageNumberButton = styled(Button)<{ selected: boolean }>`
  background-color: transparent;
  color: ${({ theme, selected }) => (selected ? theme.colors.white : theme.colors.navy)};
  padding: 0;
  height: 4rem;
  transition: transform 0.15s ease, opacity 0.15s ease;

  &:hover,
  &:focus {
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
    opacity: 0.8;
  }
`;

const PageNumber = styled(Text)<{ selected: boolean }>`
  background-color: ${({ theme, selected }) => (selected ? theme.colors.navy : 'transparent')};
  padding: 0.9rem 1.2rem;
  line-height: 2.1rem;
  height: 100%;
`;

const EllipsisButton = styled(Button)`
  position: relative;
  bottom: 0.4rem; /* magic number to vertically align the ellipsis */
  background-color: transparent;
  color: ${({ theme }) => theme.colors.darkBlack};
  padding: 0 1rem;
  min-width: auto;
  height: 3.2rem;
  border-radius: ${({ theme }) => theme.borders.radius.tiny}px;
  transition: background-color 0.15s ease, color 0.15s ease, transform 0.15s ease;

  &:hover,
  &:focus {
    background-color: ${({ theme }) => theme.colors.almostWhite};
    color: ${({ theme }) => theme.colors.primaryPurple};
    transform: translateY(-1px);
  }

  &:active {
    background-color: ${({ theme }) => theme.colors.lightBlue};
    transform: translateY(0);
  }
`;

const EllipsisText = styled(Text)`
  letter-spacing: 0.08em;
`;

const JumpForm = styled.form`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.tiny}px;
  padding: ${({ theme }) => theme.spacing.tiny}px ${({ theme }) => theme.spacing.small}px;
  border: 1px solid ${({ theme }) => theme.colors.lightBlue};
  border-radius: ${({ theme }) => theme.borders.radius.small}px;
  background-color: ${({ theme }) => theme.colors.white};
`;

const JumpLabel = styled(Text)`
  white-space: nowrap;
`;

const JumpInput = styled(Input)`
  width: 5.6rem;
  min-width: 5.6rem;
  height: 2.8rem;
  padding: 0 ${({ theme }) => theme.spacing.small}px;
  text-align: center;
`;

const JumpActionButton = styled(Button)<{ $variant: 'confirm' | 'cancel' }>`
  width: 2.8rem;
  min-width: 2.8rem;
  height: 2.8rem;
  border: 1px solid ${({ theme, $variant }) => ($variant === 'confirm' ? theme.colors.amaGreen : theme.colors.greyBlue)};
  border-radius: ${({ theme }) => theme.borders.radius.tiny}px;
  background-color: ${({ theme, $variant }) => ($variant === 'confirm' ? theme.colors.amaGreen : theme.colors.almostWhite)};
  color: ${({ theme, $variant }) => ($variant === 'confirm' ? theme.colors.white : theme.colors.darkBlack)};
  padding: 0;
  transition: filter 0.15s ease, background-color 0.15s ease;

  &:hover,
  &:focus {
    filter: brightness(0.96);
  }

  &:active {
    filter: brightness(0.92);
  }
`;

const JumpActionText = styled(Text)<{ $variant: 'confirm' | 'cancel' }>`
  color: ${({ theme, $variant }) => ($variant === 'confirm' ? theme.colors.white : theme.colors.darkBlack)};
  line-height: 1;
`;

const JumpCancelIcon = styled(IconClose)`
  width: 12px;
  height: 12px;
`;

export const Pagination: React.FunctionComponent<Props> = ({ currentPage, pageCount, onPageChanged }) => {
  const [expandedEllipsisKey, setExpandedEllipsisKey] = useState<string | null>(null);
  const [jumpPageValue, setJumpPageValue] = useState('');

  useEffect(() => {
    setExpandedEllipsisKey(null);
    setJumpPageValue('');
  }, [currentPage, pageCount]);

  const decrementPage = () => {
    if (currentPage > 1) {
      onPageChanged(currentPage - 1);
    }
  };

  const incrementPage = () => {
    if (currentPage !== pageCount) {
      onPageChanged(currentPage + 1);
    }
  };

  const submitJumpPage = () => {
    const targetPage = Number(jumpPageValue.trim());
    if (!Number.isInteger(targetPage) || targetPage < 1 || targetPage > pageCount) {
      return;
    }

    onPageChanged(targetPage);
  };

  const buildPageRange = ({ currentPage, pageCount, onPageChanged, delta = 2 }: Props & { delta?: number }) => {
    const left = currentPage - delta;
    const right = currentPage + delta + 1;
    const range = [];
    const pages = [];
    let lastPageInRange: number | undefined;

    for (let i = 1; i <= pageCount; i++) {
      if (i === 1 || i === pageCount || (i >= left && i < right)) {
        range.push(i);
      }
    }

    for (const i of range) {
      if (lastPageInRange && i - lastPageInRange !== 1) {
        const ellipsisKey = `${lastPageInRange + 1}-${i - 1}`;
        const isExpanded = expandedEllipsisKey === ellipsisKey;

        pages.push(
          isExpanded ? (
            <JumpForm
              key={`jump-${ellipsisKey}`}
              onSubmit={(event) => {
                event.preventDefault();
                submitJumpPage();
              }}
            >
              <JumpLabel textStyle="smallContent">Go to page:</JumpLabel>
              <JumpInput
                name={`jump-to-page-${ellipsisKey}`}
                value={jumpPageValue}
                onChange={(event) => setJumpPageValue((event.target as HTMLInputElement).value.replace(/\D/g, '').slice(0, 4))}
                maxLength={4}
                aria-label="Go to page"
              />
              <JumpActionButton
                buttonStyle="icon"
                type="submit"
                $variant="confirm"
                ariaLabel="Go to page"
                title="Go to page"
                disabled={jumpPageValue.trim().length === 0}
              >
                <JumpActionText textStyle="smallTitle" $variant="confirm">
                  &gt;
                </JumpActionText>
              </JumpActionButton>
              <JumpActionButton
                buttonStyle="icon"
                type="button"
                $variant="cancel"
                ariaLabel="Cancel page jump"
                title="Cancel page jump"
                onClick={() => {
                  setExpandedEllipsisKey(null);
                  setJumpPageValue('');
                }}
              >
                <JumpCancelIcon />
              </JumpActionButton>
            </JumpForm>
          ) : (
            <EllipsisButton
              key={`ellipsis-${ellipsisKey}`}
              buttonStyle="text"
              onClick={() => {
                setExpandedEllipsisKey(ellipsisKey);
                setJumpPageValue('');
              }}
              ariaLabel="Open go to page"
              title="Open go to page"
            >
              <EllipsisText textStyle="smallTitle">&hellip;</EllipsisText>
            </EllipsisButton>
          )
        );
      }

      const handleClick = () => {
        onPageChanged(i);
      };

      const isCurrentPage = i === currentPage;

      pages.push(
        <PageNumberButton key={i.toString()} buttonStyle="text" selected={isCurrentPage} onClick={handleClick}>
          <PageNumber color={isCurrentPage ? 'white' : 'navy'} textStyle="smallTitle" selected={isCurrentPage}>
            {i}
          </PageNumber>
        </PageNumberButton>
      );
      lastPageInRange = i;
    }

    return pages;
  };

  const pages = buildPageRange({ currentPage, pageCount, onPageChanged });

  const isFirstPage = currentPage === 1;
  const isLastPage = currentPage === pageCount;

  return pageCount > 0 ? (
    <PaginationContainer>
      <PaginationButton buttonStyle="text" disabled={isFirstPage} onClick={decrementPage}>
        <Text color="darkBlack" textStyle="standardContentBold">
          &lt; Previous
        </Text>
      </PaginationButton>
      <PaginationPageList>{pages}</PaginationPageList>
      <PaginationButton buttonStyle="text" disabled={isLastPage} onClick={incrementPage}>
        <Text color="darkBlack" textStyle="standardContentBold">
          Next &gt;
        </Text>
      </PaginationButton>
    </PaginationContainer>
  ) : null;
};

export default Pagination;
